import { httpClient } from "@/infrastructure/http/http.service";
import type { SliderRecommendations } from "@/types/slider.types";
import type { HttpClient } from "@/infrastructure/http/http-client.factory";
import type { SsrClient } from "@/infrastructure/http/ssr-client.service";
import type {
  WorkPageData,
  WorkFirstChapter,
  WorkProfessionalReview,
  WorkReviewPage,
  WorkSelectorData,
} from "@/types/work.types";

const baseUrl = "tb-api/works";

export interface WorkPageSelection {
  isbn?: string;
  idIq?: number;
  idAmazon?: number;
}

/**
 * Fetches the complete server-rendered Works page payload.
 * @param idWork Work identifier from the canonical route.
 * @param requestClient Browser client or policy-aware SSR client.
 * @param selection Optional SSR-visible edition selection from the page query.
 * @returns Work metadata and the available format/condition selector.
 */
export async function getWorkPageData(
  idWork: number,
  requestClient: Pick<SsrClient | HttpClient, "get"> = httpClient,
  selection?: WorkPageSelection,
): Promise<WorkPageData> {
  const params =
    selection?.isbn || selection?.idIq || selection?.idAmazon
      ? {
          isbn: selection.isbn,
          idIq: selection.idIq,
          idAmazon: selection.idAmazon,
        }
      : undefined;
  const response = await requestClient.get<WorkPageData>(
    `${baseUrl}/${idWork}/`,
    {
      ...(params ? { params } : {}),
      headers: process.env.INTERNAL_API_KEY
        ? { "X-Internal-Api-Key": process.env.INTERNAL_API_KEY }
        : undefined,
    },
  );

  return response.data;
}

/**
 * Resolves selector-only data for Legacy fragment identifiers absent from the SSR
 * payload. The API query parameters transport those client-only values without
 * requesting the rest of the Works page again.
 * @param idWork Work identifier from the canonical route.
 * @param selection ISBN, edition, and optional inventory identifiers from the fragment.
 * @param requestClient Browser HTTP client.
 * @returns A ranked selector, or null when the identifier is not on this work.
 */
export async function getWorkSelectorData(
  idWork: number,
  selection: WorkPageSelection,
  requestClient: Pick<HttpClient, "get"> = httpClient,
): Promise<WorkSelectorData | null> {
  const response = await requestClient.get<WorkSelectorData | null>(
    `${baseUrl}/${idWork}/selector/`,
    {
      params: {
        isbn: selection.isbn,
        idAmazon: selection.idAmazon,
        idIq: selection.idIq,
      },
    },
  );

  return response.data;
}

/**
 * Gets professional reviews for the selected edition on demand.
 * @param idAmazon Selected edition identifier.
 * @returns Professional reviews associated with the edition.
 */
export async function getWorkProfessionalReviews(
  idAmazon: number,
): Promise<WorkProfessionalReview[]> {
  const response = await httpClient.get<WorkProfessionalReview[]>(
    `${baseUrl}/editions/${idAmazon}/professional-reviews/`,
  );

  return response.data;
}

/**
 * Loads the selected edition's Peek Inside content when the modal is opened.
 * @param idAmazon Selected edition identifier.
 * @returns Chapter HTML and whether it came from another edition.
 */
export async function getWorkFirstChapter(
  idAmazon: number,
): Promise<WorkFirstChapter> {
  const response = await httpClient.get<WorkFirstChapter>(
    `${baseUrl}/editions/${idAmazon}/first-chapter/`,
  );

  return response.data;
}

/**
 * Loads another page of customer reviews after the server-rendered first page.
 * @param idWork Work whose reviews should be loaded.
 * @param pageNumber One-based review page number.
 * @param requestClient Browser client, injectable for tests.
 * @returns The requested customer-review page.
 */
export async function getWorkReviews(
  idWork: number,
  pageNumber: number,
  requestClient: Pick<HttpClient, "get"> = httpClient,
): Promise<WorkReviewPage> {
  const response = await requestClient.get<WorkReviewPage>(
    `${baseUrl}/${idWork}/reviews/`,
    { params: { pageNumber } },
  );

  return response.data;
}

/**
 * Follows or unfollows an author from the Works author recommendation module.
 * @param idAuthor Author identifier.
 * @param wantsFollow Desired follow state.
 * @param requestClient Browser client, injectable for tests.
 * @returns The persisted follow state.
 */
export async function setWorkAuthorFollow(
  idAuthor: number,
  wantsFollow: boolean,
  requestClient: Pick<HttpClient, "post"> = httpClient,
): Promise<boolean> {
  const response = await requestClient.post<{ isFollowing: boolean }>(
    `${baseUrl}/authors/${idAuthor}/follow/`,
    { wantsFollow },
  );

  return response.data.isFollowing;
}

/**
 * Follows or unfollows a series from the Works recommendation module.
 * @param idSeries Series identifier.
 * @param wantsFollow Desired follow state.
 * @param requestClient Browser client, injectable for tests.
 * @returns The persisted follow state.
 */
export async function setWorkSeriesFollow(
  idSeries: number,
  wantsFollow: boolean,
  requestClient: Pick<HttpClient, "post"> = httpClient,
): Promise<boolean> {
  const response = await requestClient.post<{ isFollowing: boolean }>(
    `${baseUrl}/series/${idSeries}/follow/`,
    { wantsFollow },
  );

  return response.data.isFollowing;
}

/**
 * Sets or clears the signed-in customer's helpful vote for a review.
 * @param idWorkReview Review being voted on.
 * @param voteValue One to like the review or zero to clear the vote.
 * @param requestClient Browser client, injectable for tests.
 * @returns The persisted vote value.
 */
export async function setWorkReviewVote(
  idWorkReview: number,
  voteValue: 0 | 1,
  requestClient: Pick<HttpClient, "post"> = httpClient,
): Promise<number> {
  const response = await requestClient.post<{ voteValue: number }>(
    `${baseUrl}/reviews/${idWorkReview}/vote/`,
    { voteValue },
  );

  return response.data.voteValue;
}

/**
 * Reports a customer review for moderation.
 * @param idWorkReview Review being reported.
 * @param requestClient Browser client, injectable for tests.
 * @returns Whether the review was reported successfully.
 */
export async function reportWorkReview(
  idWorkReview: number,
  requestClient: Pick<HttpClient, "post"> = httpClient,
): Promise<boolean> {
  const response = await requestClient.post<{ isReported: boolean }>(
    `${baseUrl}/reviews/${idWorkReview}/report/`,
    { flaggedReason: null },
  );

  return response.data.isReported;
}

/**
 * Hides the signed-in customer's own review using Legacy's delete behavior.
 * @param idWorkReview Review being hidden.
 * @param requestClient Browser client, injectable for tests.
 * @returns Whether the review was hidden successfully.
 */
export async function hideWorkReview(
  idWorkReview: number,
  requestClient: Pick<HttpClient, "post"> = httpClient,
): Promise<boolean> {
  const response = await requestClient.post<{ isHidden: boolean }>(
    `${baseUrl}/reviews/${idWorkReview}/hide/`,
  );

  return response.data.isHidden;
}

/**
 * Records one hydrated VNext Works visit in Redis, then loads native work-specific
 * recommendations without putting either operation on the SSR path.
 * @param idWork Work identifier used to select recommendation sets.
 * @param visitId Stable identifier for this logical navigation and any request retry.
 * @param requestClient VNext browser client, injectable for testing.
 * @returns Work recommendation sliders in the shared VNext shape.
 */
export async function getWorkSliders(
  idWork: number,
  visitId: string,
  requestClient: Pick<HttpClient, "post"> = httpClient,
): Promise<SliderRecommendations[]> {
  const response = await requestClient.post<SliderRecommendations[]>(
    `${baseUrl}/${idWork}/recommendations/`,
    { visitId, quantity: 20 },
  );

  return response.data;
}

/**
 * Loads the unfiltered Recent Browsing slider used by the add-to-cart drawer.
 * This is intentionally separate from the Works page response, whose
 * recent items may be de-duplicated against You Might Also Enjoy.
 * @param requestClient VNext browser client, injectable for testing.
 * @returns The drawer recommendation slider.
 */
export async function getWorkDrawerRecentBrowsingSlider(
  requestClient: Pick<HttpClient, "get"> = httpClient,
): Promise<SliderRecommendations> {
  const response = await requestClient.get<SliderRecommendations>(
    `${baseUrl}/recommendations/recent-browsing/`,
  );

  return response.data;
}
